interface MapData {
    address: string;
    country: string;
    province: string;
    city: string;
    zipcode: string;
    coordinate_lat: number;
    coordinate_lng: number;
};

class GoogleMapsService {
    public readonly ID = 'autocomplete-address';
    private autocomplete: google.maps.places.Autocomplete | null = null;
    private autocompleteInput: HTMLInputElement | null = null;
    private countries: string[] = ['pr']; // ['ar', 'cl', 'uy', 'py', 'pr'];
    private fields: string[] = [
        'address_components',
        'geometry',
        'icon',
        'name',
    ];

    initGoogleAutocomplete(onPlaceChanged: () => void) {
        if (!this.autocompleteInput) {
            this.autocompleteInput = document.getElementById(this.ID) as HTMLInputElement | null;
        }
        if (!this.autocompleteInput || this.autocomplete) return;
        if (!(window as any).google || !(window as any).google.maps || !(window as any).google.maps.places) return;

        var options = {
            types: ['address'],
            componentRestrictions: { country: this.countries },
        };

        this.autocomplete = new google.maps.places.Autocomplete(
            this.autocompleteInput,
            options
        );
        this.autocomplete.setFields(this.fields);
        this.autocomplete.addListener('place_changed', onPlaceChanged);
    }

    setAutocompleteInput(input: HTMLInputElement | null) {
        this.autocompleteInput = input;
    }

    private getMap() {
        return new google.maps.Map(document.getElementById('map'), {
            mapTypeId: 'roadmap',
            center: { lat: -34.6177, lng: -58.3621 },
            zoom: 3,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            scaleControl: true,
            styles: [
                {
                    featureType: 'transit',
                    stylers: [
                        {
                            visibility: 'off'
                        }
                    ]
                },
                {
                    featureType: 'poi',
                    stylers: [{ visibility: 'off' }]
                }
            ],
        });
    }

    private setMarker(place: google.maps.places.PlaceResult) {
        var map = this.getMap();
        var marker = new google.maps.Marker({
            map: map,
            anchorPoint: new google.maps.Point(0, -29),
        });

        if (place.geometry.viewport) {
            map.fitBounds(place.geometry.viewport);
        } else {
            map.setCenter(place.geometry.location);
            map.setZoom(17);
        }
        marker.setPosition(place.geometry.location);
        marker.setVisible(true);
    }

    private getAddress(place: google.maps.places.PlaceResult) {
        return place.address_components.find((e) => e.types[0] === 'route').long_name +
            ' ' +
            (place.address_components.find((e) => e.types[0] === 'street_number')
                ? place.address_components.find((e) => e.types[0] === 'street_number')
                    .long_name
                : '')
    }

    private getCountry(place: google.maps.places.PlaceResult) {
        return place.address_components.find((e) => e.types[0] === 'country')
            ? place.address_components.find((e) => e.types[0] === 'country').long_name
            : '';
    }

    private getProvince(place: google.maps.places.PlaceResult) {
        return place.address_components.find(
            (e) => e.types[0] === 'administrative_area_level_1'
        )
            ? place.address_components.find(
                (e) => e.types[0] === 'administrative_area_level_1'
            ).long_name
            : '';
    }

    private getCity(place: google.maps.places.PlaceResult) {
        return place.address_components.find(
            (e) => e.types[0] === 'administrative_area_level_2'
        )
            ? place.address_components.find(
                (e) => e.types[0] === 'administrative_area_level_2'
            ).long_name
            : '';
    }

    private getZipcode(place: google.maps.places.PlaceResult) {
        return place.address_components.find(
            (e) => e.types[0] === 'postal_code'
        )
            ? place.address_components.find((e) => e.types[0] === 'postal_code')
                .long_name
            : '';
    }

    getMapData(): MapData | undefined {
        if (!this.autocomplete) return;
        const place = this.autocomplete.getPlace();
        if (!place || !place.geometry || !place.address_components) return;

        this.setMarker(place);

        const address = this.getAddress(place);
        const country = this.getCountry(place);
        const province = this.getProvince(place);
        const city = this.getCity(place);
        const zipcode = this.getZipcode(place);
        const coordinate_lat = place.geometry.location.lat();
        const coordinate_lng = place.geometry.location.lng();

        return {
            address,
            country,
            province,
            city,
            zipcode,
            coordinate_lat,
            coordinate_lng,
        };
    }
}

export default GoogleMapsService;